/**
 * Edits the preview's mock dataset (the values the live preview fills in for
 * sample mode): binding token values and per-table sample rows. Opens in a
 * ContextDrawer from the preview toolbar; changes are merged into the workbench's
 * sample and persisted alongside the contract on save.
 */

import { Button, TextField } from '@protoss/ui';
import { ContextDrawer } from '../../../../components/shell/ContextDrawer';
import { humanizeToken, type PreviewSample } from '../../../../components/doc';

export interface SowMockDataEditorProps {
  open: boolean;
  sample: PreviewSample;
  onClose: () => void;
  onChange: (sample: PreviewSample) => void;
}

function columnsOf(rows: Array<Record<string, string>>): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

export function SowMockDataEditor({ open, sample, onClose, onChange }: SowMockDataEditorProps) {
  const tokenKeys = Object.keys(sample.tokens);
  const tableKeys = Object.keys(sample.data);

  const setToken = (key: string, value: string) =>
    onChange({ ...sample, tokens: { ...sample.tokens, [key]: value } });

  const setCell = (dataKey: string, rowIndex: number, col: string, value: string) => {
    const rows = (sample.data[dataKey] ?? []).map((r, i) =>
      i === rowIndex ? { ...r, [col]: value } : r
    );
    onChange({ ...sample, data: { ...sample.data, [dataKey]: rows } });
  };

  const addRow = (dataKey: string) => {
    const rows = sample.data[dataKey] ?? [];
    const blank = Object.fromEntries(columnsOf(rows).map((c) => [c, '']));
    onChange({ ...sample, data: { ...sample.data, [dataKey]: [...rows, blank] } });
  };

  const removeRow = (dataKey: string, rowIndex: number) => {
    const rows = (sample.data[dataKey] ?? []).filter((_, i) => i !== rowIndex);
    onChange({ ...sample, data: { ...sample.data, [dataKey]: rows } });
  };

  return (
    <ContextDrawer open={open} onClose={onClose} title="Edit mock data" width={460} trapFocus>
      <div className="ptp-sow-mock">
        <section className="ptp-sow-mock__group">
          <h4 className="ptp-sow-mock__heading">Tokens</h4>
          {tokenKeys.length === 0 ? (
            <p className="ptp-sow-mock__empty">No tokens in this template.</p>
          ) : (
            <div className="ptp-sow-mock__tokens">
              {tokenKeys.map((key) => (
                <label key={key} className="ptp-sow-mock__token">
                  <span className="ptp-sow-mock__token-label">{humanizeToken(key)}</span>
                  <TextField
                    value={sample.tokens[key] ?? ''}
                    onChange={(e) => setToken(key, e.target.value)}
                    size="compact"
                  />
                </label>
              ))}
            </div>
          )}
        </section>

        {tableKeys.map((dataKey) => {
          const rows = sample.data[dataKey] ?? [];
          const cols = columnsOf(rows);
          return (
            <section key={dataKey} className="ptp-sow-mock__group">
              <h4 className="ptp-sow-mock__heading">
                Table: <code>{dataKey}</code>
              </h4>
              <div className="ptp-sow-mock__rows">
                {rows.map((row, ri) => (
                  <div key={ri} className="ptp-sow-mock__row">
                    {cols.map((col) => (
                      <TextField
                        key={col}
                        value={row[col] ?? ''}
                        onChange={(e) => setCell(dataKey, ri, col, e.target.value)}
                        placeholder={col}
                        size="compact"
                      />
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(dataKey, ri)}>
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => addRow(dataKey)}>
                + Add row
              </Button>
            </section>
          );
        })}
      </div>
    </ContextDrawer>
  );
}
